import { Body, Link, SecondaryBody } from "@sprout/typography.tsx";
import { ComponentType } from "react";
import DreamJobIcon from "@sprout/icons/dream_job.tsx";
import CalendarIcon from "@sprout/icons/calendar.tsx";
import FormIcon from "@sprout/icons/form.tsx";
import ActivityIcon from "@sprout/icons/activity.tsx";
import PreviewIcon from "@sprout/icons/preview.tsx";
import { useTranslation } from "react-i18next";
import { IconProps } from "@sprout/icons/base.tsx";

export interface ActivityProps {
  date: string;
  type: ActivityType;
  statusMessage: string;
  interviewTime?: string;
  taskLink?: string | null;
}

enum ActivityType {
  Applied = "applied",
  MarkedDreamJob = "marked_dream_job",
  SubmittedAvailability = "submitted_availability",
  CompletedForm = "completed_form",
  SubmittedTakeHomeTest = "submitted_take_home_test",
  SelfScheduledInterview = "self_scheduled_interview",
  CanceledInterview = "canceled_interview",
}

type ActivityTypeInfo = {
  labelKey: string;
  ActivityTypeIcon: ComponentType<IconProps>;
  iconBackground: string;
  taskLinkLabelKey?: string;
};

const activityTypeInfoMap: Record<ActivityType, ActivityTypeInfo> = {
  [ActivityType.Applied]: {
    labelKey: "applied",
    ActivityTypeIcon: ActivityIcon,
    iconBackground: "bg-pale-blue",
  },
  [ActivityType.MarkedDreamJob]: {
    labelKey: "marked_dream_job",
    ActivityTypeIcon: DreamJobIcon,
    iconBackground: "bg-marigold-e50",
  },
  [ActivityType.SubmittedAvailability]: {
    labelKey: "submitted_availability",
    ActivityTypeIcon: CalendarIcon,
    iconBackground: "bg-pale-blue",
    taskLinkLabelKey: "submitted_availability_link",
  },
  [ActivityType.CompletedForm]: {
    labelKey: "completed_form",
    ActivityTypeIcon: FormIcon,
    iconBackground: "bg-pale-blue",
  },
  [ActivityType.SubmittedTakeHomeTest]: {
    labelKey: "submitted_take_home_test",
    ActivityTypeIcon: FormIcon,
    iconBackground: "bg-pale-blue",
  },
  [ActivityType.SelfScheduledInterview]: {
    labelKey: "self_scheduled_interview",
    ActivityTypeIcon: CalendarIcon,
    iconBackground: "bg-pale-blue",
    taskLinkLabelKey: "self_scheduled_interview_link",
  },
  [ActivityType.CanceledInterview]: {
    labelKey: "canceled_interview",
    ActivityTypeIcon: CalendarIcon,
    iconBackground: "bg-pale-blue",
  },
};

export const ActivityCard = ({ type, statusMessage, interviewTime, taskLink }: ActivityProps) => {
  const { labelKey, ActivityTypeIcon, iconBackground, taskLinkLabelKey } =
    activityTypeInfoMap[type];
  const { t } = useTranslation("applications", { keyPrefix: "activity_history" });

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className={`${iconBackground} p-1 rounded-sm`}>
          {" "}
          <ActivityTypeIcon size="sm" />
        </div>
        {type !== ActivityType.Applied && <div className="flex-1 w-px bg-gray-700 mt-1" />}
      </div>
      <div className="flex flex-col gap-1 pb-4">
        <Body>{t(labelKey)}</Body>
        {interviewTime && (
          <Body className={type === ActivityType.CanceledInterview ? "line-through" : undefined}>
            {interviewTime}
          </Body>
        )}
        <SecondaryBody className="!text-xs">{statusMessage}</SecondaryBody>
        {taskLink && taskLinkLabelKey && (
          <Link
            className="!text-xs inline-flex items-center gap-1 [&>svg]:!size-4 [&>svg]:!-top-0.5"
            href={taskLink}
            external={true}
          >
            {t(taskLinkLabelKey)}
            <PreviewIcon size="xs" />
          </Link>
        )}
      </div>
    </div>
  );
};
